import { GridColDef } from '@mui/x-data-grid';
import DataGrid from '../../shared/DataGrid/StyledDataGrid';
import { Button, Typography } from '@mui/material';
import { DataGridContainer } from '../RestaurantsPage/tabs/Info';
import { TopBar } from '../ProductsPage';
import { useDeleteGlobalExceptionHours, useGlobalExceptionHours } from '../../apis/shops-api';
import { useTranslation } from 'react-i18next';
import GlobalExceptionHoursModal from './GlobalExceptionHoursModal';
import { useState } from 'react';
import { checkPermissions, SHOPS_WRITE } from '../../shared/utils/permissions';
import { useUserPermissions } from '../../apis/user-details';
import { hourTypeLabel } from '../SingleRestaurantPage/tabs/Hours/utils';
import dayjs from 'dayjs';
import { GetGlobalOpeningHoursExceptionDto } from '@kotipizzagroup/kotipizza-shops-api-admin-client';
import ExperimentalFeatureBanner from '../../shared/ExperimentalFeatureBanner/ExperimentalFeatureBanner';

export type ExceptionItem = GetGlobalOpeningHoursExceptionDto;

const GlobalExceptionHoursPage: React.FC = () => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<ExceptionItem | null>(null);
  const { data: exceptions } = useGlobalExceptionHours();
  const { roles: userPermissions } = useUserPermissions();
  const { t } = useTranslation();

  const deleteHours = useDeleteGlobalExceptionHours();

  const isLoading = isModalOpen || deleteHours.isLoading;

  const handleModalOpen = () => {
    setSelected(null);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setSelected(null);
    setModalOpen(false);
  };

  const columns: GridColDef<ExceptionItem>[] = [
    {
      field: 'date',
      headerName: t('globalExceptions.tableHeader.date'),
      flex: 1,
      renderCell: (params) => {
        return (
          <span style={{ opacity: params.row.isPast ? 0.5 : 1 }}>{dayjs(params.row.date).format('DD.MM.YYYY')}</span>
        );
      },
    },
    {
      field: 'type',
      headerName: t('globalExceptions.tableHeader.type'),
      flex: 1,
      renderCell: (params) => {
        return <span style={{ opacity: params.row.isPast ? 0.5 : 1 }}>{hourTypeLabel(params.row.type)}</span>;
      },
    },
    {
      field: 'startTime',
      headerName: t('globalExceptions.tableHeader.startTime'),
      flex: 1,
      renderCell: (params) => {
        return (
          <span style={{ opacity: params.row.isPast ? 0.5 : 1 }}>
            {params.row.isClosed ? t('Closed') : params.row.startTime}
          </span>
        );
      },
    },
    {
      field: 'endTime',
      headerName: t('globalExceptions.tableHeader.endTime'),
      flex: 1,
      renderCell: (params) => {
        return (
          <span style={{ opacity: params.row.isPast ? 0.5 : 1 }}>
            {params.row.isClosed ? t('Closed') : params.row.endTime}
          </span>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      flex: 1,
      renderCell: (params) => (
        <>
          {!params.row.isPast && (
            <Button onClick={() => handleEdit(params.row)} disabled={isLoading}>
              {t('global.edit')}
            </Button>
          )}
          <Button onClick={() => handleDelete(params.row)} color="error" disabled={isLoading}>
            {t('global.delete')}
          </Button>
        </>
      ),
    },
  ];

  const handleDelete = (data: ExceptionItem) => {
    deleteHours.mutate(data.id);
  };

  const handleEdit = (data: ExceptionItem) => {
    setSelected(data);
    setModalOpen(true);
  };

  const rows = exceptions || [];

  return (
    <>
      <TopBar>
        <Typography variant="h1">{t('globalExceptions.page.title')}</Typography>
        {checkPermissions(userPermissions || [], [SHOPS_WRITE]) && (
          <Button variant="contained" disabled={isLoading} onClick={handleModalOpen} color="primary">
            {t('global.add')}
          </Button>
        )}
      </TopBar>
      <ExperimentalFeatureBanner />
      <DataGridContainer>
        <DataGrid
          columns={columns}
          rows={rows}
          initialState={{
            sorting: {
              sortModel: [{ field: 'date', sort: 'desc' }],
            },
          }}
        />
      </DataGridContainer>
      <GlobalExceptionHoursModal isOpen={isModalOpen} onClose={handleModalClose} item={selected} />
    </>
  );
};

export default GlobalExceptionHoursPage;
